import React, { useContext, useEffect, useState } from "react"
import tw from "twin.macro"
import { Offer } from "../../../../global"
import {
  formatSeriesSlug,
  generateInventoryLink,
  reduceFeaturedModelGroups,
} from "../../../../helpers"
import { HeroCarouselProps } from "./HeroCarousel.d"
import { Image } from "../../../atoms/Image"
import { LocalInventoryLinks } from "../../../molecules/LocalInventoryLinks"
import { PriceAndMPG } from "../../../molecules/PriceAndMPG"
import { LocationContext } from "../../../../contexts/Location"
import { InventoryClient } from "../../../../clients/InventoryClient"
import { motion } from "framer-motion"
import { LanguageContext } from "../../../../contexts/Language"
import NewHeroCarousel from "../../../NewCarousel/NewHeroCarousel"
import { OffersClient } from "../../../../clients/OffersClient"
import FeaturedOfferCard from "./FeaturedOfferCard"
import { HeroImages } from "../../../NewCarousel/NewCarousel.d"
import { HeroCarouselItem } from "../../Homepage/HeroCarousel/HeroCarousel.d"
import LeaseFeatureOfferCardVariant from "./LeaseFeaturedOfferCardVariant"
import useTealiumEvent from "../../../../hooks/Tealium/useTealiumEvent"
import { useTealiumContext } from "../../../../contexts/Tealium"
import useVehicleData from "../../../../hooks/Tealium/useVehicleData"
import LeaseFeaturedMobileLinks from "./LeaseFeaturedMobileLinks"

const HeroCarousel = ({
  slides,
  extraTealiumValues,
}: HeroCarouselProps): JSX.Element => {
  // Dealers/inventory CTAs logic
  const [{ city, dealers }] = useContext(LocationContext)
  const [series, setSeries] = useState<string | undefined>()
  const [seriesYear, setSeriesYear] = useState<string | undefined>()
  const { _, language } = useContext(LanguageContext)
  const [offersCount, setOffersCount] = useState<number>(0)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [featuredOffer, setFeaturedOffer] = useState<Offer | undefined>()

  // Get the current slide's data without causing render-time state updates
  useEffect(() => {
    if (slides && slides[currentSlideIndex]) {
      // Get vehicle reference/series reference in carousel item
      const seriesData = (slides[currentSlideIndex] as HeroCarouselItem)
        ?.vehicleRef
      setSeries(seriesData?.series?.name)
      setSeriesYear(seriesData?.year?.toString())
    }
  }, [currentSlideIndex, slides])

  const getCount = async () => {
    const dealerIds = dealers?.map(dealer => dealer?.DealerCode).join(",")

    if (dealerIds?.length > 0 && series) {
      try {
        setInventoryCount("...")
        const { count } = await InventoryClient.getCount("", {
          dealer: dealerIds,
          series: series.toLowerCase(),
        })
        setInventoryCount(count)
      } catch (error) {
        setInventoryCount(error)
      }
    }
  }

  const [inventoryCount, setInventoryCount] = useState<
    string | number | (() => Promise<void>)
  >(() => getCount)

  useEffect(() => {
    getCount()
  }, [dealers, series])

  // Fetch offers data when series or seriesYear changes
  useEffect(() => {
    const getHeroOffersData = async () => {
      if (!series || !seriesYear || !slides[currentSlideIndex]) return

      try {
        const offersHeroData = await OffersClient.getHeroOffers({
          series: JSON.stringify(series),
          year: seriesYear,
          type: JSON.stringify([slides[currentSlideIndex]?.offerType || "APR"]),
          seriesCount: true,
          model: JSON.stringify(slides[currentSlideIndex].model.name),
        })
        if (offersHeroData.offers && offersHeroData.totalCount) {
          setFeaturedOffer(offersHeroData.offers[0])
          setOffersCount(offersHeroData.totalCount)
        }
      } catch (error) {
        console.error("Error fetching offers data", error)
      }
    }

    series && seriesYear && getHeroOffersData()
  }, [series, seriesYear, currentSlideIndex, slides])

  // Tealium
  const { trackTealEvent } = useTealiumEvent()
  const { tealPageData } = useTealiumContext()
  const [vehicleData] = useVehicleData()

  // Process the featured offer to get sorted rates
  /* Sorts by lowest rate and longest term and binds first item */
  const sortedRates = featuredOffer?.termRates?.sort(
    (a: { rate: number }, b: { rate: number }) => a.rate - b.rate,
  )
  const lowestRates = sortedRates?.filter(
    offer => offer && sortedRates[0] && offer.rate === sortedRates[0].rate,
  )
  const sortedTermRate = lowestRates?.sort(
    (a: { term: number }, b: { term: number }) => b.term - a.term,
  )?.[0]

  // Handle slide change in one place only
  const handleSlideChange = (newIndex: number) => {
    setCurrentSlideIndex(newIndex)
    // Fetch data related to this slide if needed
  }
  return (
    <NewHeroCarousel
      slides={slides}
      hero
      continuous
      fromOffers={true}
      activeSlide={currentSlideIndex}
      setActiveSlide={handleSlideChange}
      css={[
        tw`h-auto min-h-[780px] bg-black overflow-y-visible`,
        tw`md:min-h-[800px]`,
        tw`xl:(h-[500px] min-h-0)`,
      ]}
    >
      {({ slideIndex }) => {
        // Update currentSlideIndex via effect to avoid render-time state updates
        useEffect(() => {
          if (slideIndex !== currentSlideIndex) {
            setCurrentSlideIndex(slideIndex)
          }
        }, [slideIndex])
        // Background image, desktop
        const desktopImage: HeroImages = slides[slideIndex]?.desktopImage?.image

        // Background image, mobile
        const mobileImage: HeroImages =
          slides[slideIndex]?.mobileImage?.image ||
          slides[slideIndex]?.desktopImage?.image

        // Vehicle image, desktop
        const desktopVehicle: HeroImages =
          slides[slideIndex]?.desktopVehicle?.image

        // Vehicle image, mobile
        const mobileVehicle: HeroImages =
          slides[slideIndex]?.mobileVehicle?.image

        // Vehicle as Shown Price
        const asShownMsrp = (slides[slideIndex] as HeroCarouselItem)?.model
          ?.costs?.baseMSRP

        // Vehicle reference/series reference in carousel item
        const seriesData = (slides[slideIndex] as HeroCarouselItem)?.vehicleRef

        // Models object with all model groups reduced to single models
        const models = reduceFeaturedModelGroups(seriesData?.models)

        // User-selected featured model
        const featuredModel = models?.find(
          (node: { isFeatured: boolean }) => node?.isFeatured,
        )?.model?.series

        const seriesSlug = generateInventoryLink({
          series: formatSeriesSlug(featuredOffer?.series?.name || series),
        })
        return (
          <section
            css={[
              tw`grid grid-cols-2 overflow-y-visible scrollbar-hide h-[500px] mx-auto relative`,
            ]}
          >
            {/* Background image, desktop */}
            {desktopImage && (
              <div css={[tw`w-full absolute z-0 `]}>
                <Image
                  imageData={desktopImage}
                  css={[tw` hidden xl:(inline h-[500px] w-full  object-cover)`]}
                />
              </div>
            )}

            {/* Background image, mobile */}
            {mobileImage && (
              <Image
                imageData={mobileImage}
                css={[
                  tw`col-start-1 col-end-3 row-start-1 row-end-7 w-full h-full inline object-cover  xl:hidden`,
                ]}
              />
            )}
            {/* Vehicle image, desktop */}
            {desktopVehicle && (
              <motion.div
                animate={{ opacity: 1, x: -210, y: 85, scale: 1 }}
                initial={{ opacity: 0, x: 0, y: 75, scale: 0.8 }}
                transition={{ duration: 1, delay: 0.5, type: "spring" }}
                css={[
                  tw`col-start-2 col-span-1 row-start-2 row-end-7 mt-10 relative w-full hidden h-[350px] z-20 xl:(inline) pointer-events-none`,
                ]}
              >
                <Image
                  imageData={desktopVehicle}
                  css={[tw`object-cover absolute top-0 z-20 max-h-[350px]`]}
                />
              </motion.div>
            )}
            {/* Vehicle image, mobile */}
            <div
              css={[
                tw`mb-28 pl-16 col-start-1 col-end-3 row-start-4 row-end-7 overflow-hidden flex justify-center items-center max-h-[360px] z-20 pointer-events-none `,
                tw`md:(mb-16 ml-32)`,
                tw`lg:(ml-40 max-h-[300px])`,
                tw` xl:hidden`,
              ]}
            >
              <Image
                imageData={mobileVehicle || desktopVehicle}
                css={[
                  tw`max-h-[360px] scale-[130%] object-contain md:(max-h-[300px] scale-[100%]) xl:(scale-[120%])`,
                ]}
              />
            </div>
            {/* Featured offer card */}
            {featuredOffer?.type == "Lease" ? (
              <LeaseFeatureOfferCardVariant
                featuredOfferType={featuredOffer?.type}
                featuredOfferCTAs={
                  featuredOffer?.cta ? [featuredOffer.cta] : []
                }
                featuredOffer={featuredOffer}
                sortedTermRate={sortedTermRate}
                dealers={dealers}
                city={city}
                inventoryCount={inventoryCount}
                inventoryLink={seriesSlug}
                offersLength={offersCount}
                year={seriesYear}
                slideIndex={slideIndex}
                extraTealiumValues={extraTealiumValues}
                ctaTextPrimary={slides[slideIndex]?.ctaTextPrimary}
                ctaTextSecondary={slides[slideIndex]?.ctaTextSecondary}
              />
            ) : (
              <FeaturedOfferCard
                featuredOfferType={featuredOffer?.type}
                featuredOffer={featuredOffer}
                sortedTermRate={sortedTermRate}
                dealers={dealers}
                city={city}
                inventoryCount={inventoryCount}
                inventoryLink={seriesSlug}
                offersLength={offersCount}
                year={seriesYear}
                slideIndex={slideIndex}
                extraTealiumValues={extraTealiumValues}
                ctaTextPrimary={slides[slideIndex]?.ctaTextPrimary}
                ctaTextSecondary={slides[slideIndex]?.ctaTextSecondary}
              />
            )}
            {/* MSRP and MPG city/highway */}
            {featuredOffer?.type == "Lease" ? (
              <LeaseFeaturedMobileLinks
                seriesData={seriesData}
                asShownMsrp={asShownMsrp}
                featuredModel={featuredModel}
                featuredOffer={featuredOffer}
                seriesSlug={seriesSlug}
                inventoryCount={inventoryCount}
                slideIndex={slideIndex}
                extraTealiumValues={extraTealiumValues}
              />
            ) : (
              <motion.section
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 1, delay: 0.2, type: "spring" }}
                css={[
                  tw`col-start-1 col-end-3 row-start-5 row-end-6 flex flex-col justify-self-end content-center items-center z-10 bg-black bg-opacity-100 px-20 w-full pt-32`,
                  tw`lg:(pb-16 justify-self-end)`,
                  tw`xl:(z-20 col-start-2 col-end-3 row-start-6 row-end-7 p-6 pb-6 mr-40  w-auto bg-black bg-opacity-80 backdrop-blur-[1px] rounded-t-lg)`,
                  tw`2xl:(ml-96 justify-self-start)`,
                  tw`desktop-hd:(ml-96 justify-self-start)`,
                ]}
              >
                <PriceAndMPG
                  css={[
                    tw`flex-row pt-16 md:pt-24 lg:pt-20 xl:pt-0 `, // Over-ride default component styles
                  ]}
                  msrp={seriesData?.series?.msrp}
                  asShownMsrp={asShownMsrp}
                  mpgCity={featuredModel?.mpgCity}
                  mpgHighway={featuredModel?.mpgHighway}
                  range={featuredModel?.range}
                  light
                  small
                  seriesName={
                    featuredOffer?.series?.name ||
                    seriesData?.series?.name ||
                    ""
                  }
                  seriesYear={featuredOffer?.year?.toString()}
                />
                <div css={[tw`text-center text-xs italic text-gray-500`]}>
                  {featuredOffer?.series?.currentYear}{" "}
                  {featuredOffer?.series?.name} {_("shown with options.")}
                </div>
                {/* CTAs */}
                <section
                  css={[
                    tw`col-start-1 col-end-3 row-start-6 row-end-7 flex flex-wrap justify-center gap-3 pt-12 bg-black`,
                    tw`lg:py-5`,
                    tw`xl:hidden`,
                  ]}
                >
                  {/* TODO: Change secondary button to "Details" as per mockup. Offers template not currently up, so will come back to this when it is. */}

                  <LocalInventoryLinks
                    city={city}
                    dealerCount={dealers?.length}
                    inventoryLink={seriesSlug}
                    inventoryCount={inventoryCount}
                    hideLocation
                    ctaTextPrimary={slides[slideIndex]?.ctaTextPrimary}
                    ctaTextSecondary={slides[slideIndex]?.ctaTextSecondary}
                    series={featuredOffer?.series?.name}
                    year={featuredOffer?.series?.currentYear}
                    css={[
                      `& > div {
                  padding: 0;
                }`,
                    ]}
                    analyticsId={`offers:${slideIndex + 1}`}
                    extraTealiumValues={extraTealiumValues}
                    fromOffers={true}
                  />
                </section>
              </motion.section>
            )}
          </section>
        )
      }}
    </NewHeroCarousel>
  )
}

export default HeroCarousel
